/**
 * Brand Theme Provider
 * Fetches brand configuration and applies CSS variables for theming
 */

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { getSessionId } from "./utils";
import { config, getApiUrl } from "./config";
import * as Sentry from "@sentry/react";

export interface BrandTheme {
  id: string;
  name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string | null;
  accent_color: string | null;
}

interface BrandThemeContextValue {
  theme: BrandTheme | null;
  isLoading: boolean;
  error: string | null;
}

const BrandThemeContext = createContext<BrandThemeContextValue>({
  theme: null,
  isLoading: true,
  error: null,
});

// eslint-disable-next-line react-refresh/only-export-components
export function useBrandTheme() {
  return useContext(BrandThemeContext);
}

/**
 * Convert hex color to HSL for Tailwind CSS variables
 */
function hexToHsl(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Calculate logo background color based on brand primary color.
 * Uses a darker background for dark brand colors (common with white logos)
 * and a lighter background for light brand colors (common with dark logos).
 */
function getLogoBackgroundColor(hex: string): string {
  hex = hex.replace(/^#/, "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // Dark brand colors typically pair with white/light logos → use dark bg
  // Light brand colors typically pair with dark logos → use light bg
  return luminance > 0.5 ? "#f3f4f6" : "#1f2937"; // gray-100 or gray-800
}

/**
 * Apply brand colors as CSS custom properties
 */
function applyBrandColors(theme: BrandTheme) {
  const root = document.documentElement;

  if (theme.primary_color) {
    const primaryHsl = hexToHsl(theme.primary_color);
    const logoBg = getLogoBackgroundColor(theme.primary_color);

    // Set the main primary color (used by index.css)
    root.style.setProperty("--color-primary", theme.primary_color);
    root.style.setProperty("--color-accent", theme.primary_color);

    // Set logo background to contrast with primary brand color
    root.style.setProperty("--color-logo-bg", logoBg);

    // Generate hover and light variants
    root.style.setProperty("--color-primary-hover", darkenColor(theme.primary_color, 0.1));
    root.style.setProperty("--color-primary-light", lightenColor(theme.primary_color, 0.4));

    // Set brand color scale
    root.style.setProperty("--color-brand-500", theme.primary_color);
    root.style.setProperty("--color-brand-600", darkenColor(theme.primary_color, 0.1));
    root.style.setProperty("--color-brand-700", darkenColor(theme.primary_color, 0.2));
    root.style.setProperty("--color-brand-400", lightenColor(theme.primary_color, 0.1));
    root.style.setProperty("--color-brand-300", lightenColor(theme.primary_color, 0.25));
    root.style.setProperty("--color-brand-200", lightenColor(theme.primary_color, 0.4));
    root.style.setProperty("--color-brand-100", lightenColor(theme.primary_color, 0.6));
    root.style.setProperty("--color-brand-50", lightenColor(theme.primary_color, 0.85));

    // Legacy brand variables (for backwards compatibility)
    root.style.setProperty("--brand-primary", primaryHsl);
  }

  if (theme.secondary_color) {
    const secondaryHsl = hexToHsl(theme.secondary_color);

    // Set the main secondary color
    root.style.setProperty("--color-secondary", theme.secondary_color);

    // Generate hover and light variants
    root.style.setProperty("--color-secondary-hover", darkenColor(theme.secondary_color, 0.1));
    root.style.setProperty("--color-secondary-light", lightenColor(theme.secondary_color, 0.4));

    // Set secondary color scale
    root.style.setProperty("--color-secondary-500", theme.secondary_color);
    root.style.setProperty("--color-secondary-600", darkenColor(theme.secondary_color, 0.1));
    root.style.setProperty("--color-secondary-700", darkenColor(theme.secondary_color, 0.2));
    root.style.setProperty("--color-secondary-400", lightenColor(theme.secondary_color, 0.1));
    root.style.setProperty("--color-secondary-300", lightenColor(theme.secondary_color, 0.25));
    root.style.setProperty("--color-secondary-200", lightenColor(theme.secondary_color, 0.4));
    root.style.setProperty("--color-secondary-100", lightenColor(theme.secondary_color, 0.6));
    root.style.setProperty("--color-secondary-50", lightenColor(theme.secondary_color, 0.85));

    // Legacy brand variables (for backwards compatibility)
    root.style.setProperty("--brand-secondary", secondaryHsl);
  }

  if (theme.accent_color) {
    const accentHsl = hexToHsl(theme.accent_color);

    // Set the main accent color
    root.style.setProperty("--color-accent-custom", theme.accent_color);

    // Generate hover and light variants
    root.style.setProperty("--color-accent-hover", darkenColor(theme.accent_color, 0.1));
    root.style.setProperty("--color-accent-light", lightenColor(theme.accent_color, 0.4));

    // Set accent color scale
    root.style.setProperty("--color-accent-500", theme.accent_color);
    root.style.setProperty("--color-accent-600", darkenColor(theme.accent_color, 0.1));
    root.style.setProperty("--color-accent-700", darkenColor(theme.accent_color, 0.2));
    root.style.setProperty("--color-accent-400", lightenColor(theme.accent_color, 0.1));
    root.style.setProperty("--color-accent-300", lightenColor(theme.accent_color, 0.25));
    root.style.setProperty("--color-accent-200", lightenColor(theme.accent_color, 0.4));
    root.style.setProperty("--color-accent-100", lightenColor(theme.accent_color, 0.6));
    root.style.setProperty("--color-accent-50", lightenColor(theme.accent_color, 0.85));

    // Legacy brand variables (for backwards compatibility)
    root.style.setProperty("--brand-accent", accentHsl);
  }
}

/**
 * Lighten a hex color by a factor (0-1)
 */
function lightenColor(hex: string, factor: number): string {
  hex = hex.replace(/^#/, "");
  const r = Math.min(
    255,
    Math.round(
      parseInt(hex.substring(0, 2), 16) + (255 - parseInt(hex.substring(0, 2), 16)) * factor
    )
  );
  const g = Math.min(
    255,
    Math.round(
      parseInt(hex.substring(2, 4), 16) + (255 - parseInt(hex.substring(2, 4), 16)) * factor
    )
  );
  const b = Math.min(
    255,
    Math.round(
      parseInt(hex.substring(4, 6), 16) + (255 - parseInt(hex.substring(4, 6), 16)) * factor
    )
  );
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/**
 * Darken a hex color by a factor (0-1)
 */
function darkenColor(hex: string, factor: number): string {
  hex = hex.replace(/^#/, "");
  const r = Math.max(0, Math.round(parseInt(hex.substring(0, 2), 16) * (1 - factor)));
  const g = Math.max(0, Math.round(parseInt(hex.substring(2, 4), 16) * (1 - factor)));
  const b = Math.max(0, Math.round(parseInt(hex.substring(4, 6), 16) * (1 - factor)));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

interface BrandThemeProviderProps {
  children: ReactNode;
}

export function BrandThemeProvider({ children }: BrandThemeProviderProps) {
  const [theme, setTheme] = useState<BrandTheme | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchBrandTheme() {
      const sessionId = getSessionId();
      if (!sessionId) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch session to get brand info
        const sessionToken = localStorage.getItem("session_token");
        if (!sessionToken) {
          setIsLoading(false);
          return;
        }

        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          [config.sessionTokenHeader]: sessionToken,
        };

        // Add Supabase anon key if configured
        if (config.supabaseAnonKey) {
          headers["Authorization"] = `Bearer ${config.supabaseAnonKey}`;
        }

        const url = getApiUrl(`/sessions/${sessionId}`);
        let response: Response;
        try {
          response = await fetch(url, {
            method: "GET",
            headers,
          });

          if (!response.ok) {
            throw new Error("Failed to fetch session");
          }
        } catch (error) {
          if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
            Sentry.captureException(error, {
              tags: { component: "BrandThemeProvider", action: "fetchSession", sessionId },
              extra: { url },
            });
          }
          throw error;
        }

        let data;
        try {
          data = await response.json();
        } catch (error) {
          if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
            Sentry.captureException(error, {
              tags: { component: "BrandThemeProvider", action: "parseSessionResponse", sessionId },
              extra: { url },
            });
          }
          throw error;
        }
        console.log("brand theme data", data);
        if (data.success && data.data?.session) {
          const brandTheme: BrandTheme = {
            id: data.data.session.retailer_id,
            name: data.data.retailer?.name || "Store",
            logo_url: data.data.retailer?.logo_url || null,
            primary_color: data.data.retailer?.primary_color || "#0ea5e9",
            secondary_color: data.data.retailer?.secondary_color || null,
            accent_color: data.data.retailer?.accent_color || null, //84BF41
          };

          setTheme(brandTheme);
          applyBrandColors(brandTheme);
        }
      } catch (err) {
        console.warn("Failed to fetch brand theme:", err);
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(err, {
            tags: { component: "BrandThemeProvider", action: "fetchBrandTheme", sessionId },
          });
        }
        setError(err instanceof Error ? err.message : "Unknown error");
        // Apply default theme
        applyBrandColors({
          id: "",
          name: "Default",
          logo_url: null,
          primary_color: "#0ea5e9",
          secondary_color: null,
          accent_color: null,
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchBrandTheme();
  }, []);

  return (
    <BrandThemeContext.Provider value={{ theme, isLoading, error }}>
      {children}
    </BrandThemeContext.Provider>
  );
}
